import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { useModernSubscription } from '../hooks/useModernSubscription';
import { useUser } from '../hooks/useUser';
import { useAccountType } from '../hooks/useAccountType';
import { useToast } from '../hooks/useToast';
import { usePendingTransactions, PendingTransaction } from '../hooks/usePendingTransactions';
import { UserCircle, Download, CreditCard, User, History, FileText, Loader2, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle, Smartphone, Info, X, Trash2 } from 'lucide-react';
import Receipt, { ReceiptRef } from '../components/abonnement/Receipt';
import PaymentModal, { DEFAULT_PAYMENT_TYPE, DEFAULT_COUNTRY_CODE } from '../components/ui/PaymentModal';
import { PaymentRecord, PricingPlan, PaymentDetails } from '../types/profile';
import { PageContainer } from '../components/layout/PageContainer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import { CustomerType } from '../types/subscription';

// Import des données de tarification
import { getAllPlans } from '../components/home/Pricing';

// Composant PlanSelector simplifié
function PlanSelector({ plans, currentPlan, onSelect }: { plans: PricingPlan[]; currentPlan: string; onSelect: (plan: PricingPlan) => void }) {
  // ✨ Montrer tous les plans incluant Freemium (gratuit)
  // Exclure seulement les plans "Sur devis" qui sont custom
  const standardPlans = plans.filter(plan => plan.price !== 'Sur devis');
  
  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Plans disponibles</h3>
        <p className="text-sm sm:text-base text-gray-600">Choisissez le plan qui correspond le mieux à vos besoins</p>
      </div>
      
      {standardPlans.length === 0 ? (
        <div className="text-center py-6 sm:py-8">
          <p className="text-sm sm:text-base text-gray-600">Aucun plan disponible pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {standardPlans.map((plan, index) => {
            const isCurrent = plan.name.toLowerCase() === currentPlan.toLowerCase();
            return (
              <div 
                key={`plan-${index}`} 
                className={`relative border rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow ${
                  isCurrent 
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                    : plan.highlighted
                    ? 'border-primary bg-white ring-1 ring-primary/30'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {isCurrent && (
                  <div className="absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                      Plan actuel
                    </span>
                  </div>
                )}
                
                {plan.highlighted && !isCurrent && (
                  <div className="absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                      Recommandé
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{plan.name}</h4>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">{plan.description}</p>
                  <div className="text-2xl sm:text-3xl font-bold text-primary mb-3 sm:mb-4">
                    {plan.price === '0' ? 'Gratuit' : `$${plan.price}`}
                    {plan.price !== '0' && plan.price !== 'Sur devis' && (
                      <span className="text-xs sm:text-sm font-normal text-gray-600">/mois</span>
                    )}
                  </div>
                  
                  <button
                    className={`w-full py-2 px-3 sm:px-4 rounded-md font-medium transition-colors text-sm sm:text-base ${
                      isCurrent 
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                        : 'bg-primary text-white hover:bg-primary-dark'
                    }`}
                    onClick={() => onSelect(plan)}
                    disabled={isCurrent}
                  >
                    {isCurrent ? 'Plan actuel' : plan.price === '0' ? 'Activer gratuitement' : 'Choisir ce plan'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Abonnement() {
  const { user } = useUser();
  const { accountType, isLoading: accountLoading, isNewAccount } = useAccountType();
  
  // Hooks existants + nouveau hook moderne
  const { 
    subscription: legacySubscription, 
    isLoading: legacyLoading, 
    error: subscriptionError, 
    changePlan, 
    reload 
  } = useSubscription();

  const { 
    subscription: modernSubscription, 
    tokenBalance,
    plans: modernPlans,  // ✅ Récupérer les plans depuis le hook
    isLoading: modernLoading
  } = useModernSubscription();

  // ✨ FILTRAGE PAR TYPE DE COMPTE
  // Déterminer le type de client pour le filtrage des plans
  const customerTypeFilter = useMemo(() => {
    if (isNewAccount) return null; // Nouveau compte = montrer tous les plans
    if (accountType === 'company') return CustomerType.SME;
    if (accountType === 'financial_institution') return CustomerType.FINANCIAL_INSTITUTION;
    return null;
  }, [accountType, isNewAccount]);

  // Convertir les plans modernes vers le format legacy pour compatibilité UI
  // ET filtrer selon le type de compte
  const allPricingPlans = useMemo(() => {
    let plans: PricingPlan[];
    
    if (modernPlans.length > 0) {
      // Utiliser les plans dynamiques du backend
      plans = modernPlans.map(plan => {
        // Formatage des messages IA selon la nouvelle structure
        let tokenAmount: string;
        const tokens = plan.tokenAllocation.monthlyTokens;
        if (tokens === -1) {
          tokenAmount = 'Illimité';
        } else if (tokens >= 1000) {
          tokenAmount = `${(tokens / 1000).toFixed(0)}K`;
        } else {
          tokenAmount = `${tokens}`;
        }
        
        return {
          id: plan.id,                    // 🔑 UUID requis pour POST /subscriptions/purchase
          name: plan.name,
          description: plan.description,
          price: plan.monthlyPriceUSD === 0 ? '0' : plan.monthlyPriceUSD.toString(),
          features: Object.values(plan.features)
            .filter(f => f.enabled && f.description)
            .map(f => f.description as string),
          highlighted: plan.isPopular,
          demo: plan.monthlyPriceUSD === 0,
          tokenAmount,
          configId: plan.configId,        // Config ID (ex: 'sme-basic')
          customerType: plan.customerType // Garder le type pour filtrage
        } as PricingPlan;
      });
    } else {
      // Fallback vers getAllPlans si aucun plan moderne chargé
      plans = getAllPlans() as PricingPlan[];
    }
    
    // ✨ Filtrer selon le type de compte (sauf pour les nouveaux comptes)
    if (customerTypeFilter) {
      plans = plans.filter(plan => {
        const planType = (plan as any).customerType;
        // Si le plan n'a pas de type, le montrer (pour compatibilité)
        if (!planType) return true;
        return planType === customerTypeFilter;
      });
    }
    
    return plans;
  }, [modernPlans, customerTypeFilter]);
  
  // Utiliser les données modernes quand disponibles, sinon fallback legacy
  const subscription = modernSubscription ? {
    id: modernSubscription.id,
    userId: modernSubscription.customerId,
    currentPlan: modernSubscription.plan.name,
    tokenBalance: tokenBalance?.remainingTokens || 0,
    tokenTotal: tokenBalance?.totalTokens || 0,
    paymentHistory: []
  } : legacySubscription;
  
  const isLoading = modernLoading || legacyLoading;

  // Hook pour gérer les transactions en attente
  const { 
    pendingTransactions, 
    isLoading: pendingLoading,
    checkTransactionStatus,
    removeTransaction,
    addPendingTransaction
  } = usePendingTransactions();

  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | string | null>(null);
  const [paymentType, setPaymentType] = useState<'card' | 'mobile' | 'manual'>(DEFAULT_PAYMENT_TYPE);
  const [paymentInfo, setPaymentInfo] = useState<PaymentDetails>({
    paymentMethod: DEFAULT_PAYMENT_TYPE,
    amount: '',
    cardNumber: '',
    cvv: '',
    expiryDate: '',
    mobileNumber: '',
    mobileMoneyProvider: '',
    countryCode: DEFAULT_COUNTRY_CODE,
    mobilePin: '',
  });
  const [formError, setFormError] = useState('');
  const [tab, setTab] = useState<'plan' | 'history' | 'confirm'>('plan');
  const [showReceipt, setShowReceipt] = useState<PaymentRecord | null>(null);
  const [showInstructions, setShowInstructions] = useState<PendingTransaction | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const receiptRef = useRef<ReceiptRef>(null);

  // Fonction pour activer un plan gratuit
  async function handleFreePlanActivation(plan: PricingPlan) {
    try {
      console.log('Activation du plan gratuit:', plan);
      setTab('confirm');
      reload();
    } catch (err) {
      const error = err as Error;
      setFormError(error.message || 'Erreur lors de l\'activation du plan.');
    }
  }

  // Fonction pour valider les codes promo
  function validatePromoCode(code: string): number {
    const promoCodes: Record<string, number> = {
      'WELCOME10': 0.1,  // 10% de réduction
      'SAVE20': 0.2,     // 20% de réduction
      'NEWUSER': 0.15,   // 15% de réduction
      'ANNUAL25': 0.25   // 25% de réduction pour abonnement annuel
    };
    return promoCodes[code] || 0;
  }

  // Paiement simplifié
  async function handlePaymentSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!selectedPlan || typeof selectedPlan !== 'object') return;

    // Validation selon la méthode
    if (paymentType === 'card') {
      if (!paymentInfo.cardNumber || !paymentInfo.cvv || !paymentInfo.expiryDate) {
        setFormError('Veuillez remplir tous les champs de la carte.');
        return;
      }
    } else if (paymentType === 'mobile') {
      if (!paymentInfo.mobileMoneyProvider || !paymentInfo.mobileNumber || !paymentInfo.mobilePin) {
        setFormError('Veuillez remplir tous les champs Mobile Money.');
        return;
      }
    }

    try {
      // Envoyer les détails de paiement au backend
      // Utiliser l'UUID du plan (selectedPlan.id) et non le nom
      const result = await changePlan(selectedPlan.id, paymentType, paymentInfo);
      
      // Si c'est un paiement Mobile Money en attente, sauvegarder la transaction
      if (result.pendingPayment && paymentType === 'mobile') {
        const pending = result.pendingPayment;
        addPendingTransaction({
          transactionId: pending.transactionId,
          planId: pending.planId,
          planName: selectedPlan.name,
          amount: paymentInfo.amount || selectedPlan.price,
          currency: 'USD',
          telecom: pending.telecom || 'AM',
          phoneNumber: pending.phoneNumber || paymentInfo.mobileNumber || '',
          provider: pending.provider || paymentInfo.mobileMoneyProvider || '',
          instructions: pending.instructions || pending.message || '',
          expiresAt: pending.expiresAt
        });
        
        // Fermer le modal et aller vers l'onglet Paiements
        setShowPaymentModal(false);
        setTab('history'); // Rediriger vers l'onglet Paiements pour voir la transaction
      } else {
        setShowPaymentModal(false);
        setTab('confirm');
        reload();
      }
    } catch (err) {
      // En cas d'erreur, fermer le modal et revenir aux plans
      // Le toast d'erreur est déjà affiché par le hook useSubscription
      setShowPaymentModal(false);
      setTab('plans');
    }
  }

  // Loader discret en arrière-plan - on affiche quand même le contenu avec des valeurs par défaut
  // Le chargement se fait silencieusement en background
  // Note: Les erreurs sont maintenant affichées via toast, pas en page entière

  // Utiliser des valeurs par défaut si l'abonnement n'est pas encore chargé
  const displaySubscription = subscription || {
    id: '',
    userId: user?.id || '',
    currentPlan: 'Freemium',
    tokenBalance: 0,
    tokenTotal: 100000,
    paymentHistory: []
  };

  // Indicateur de chargement très discret (petit point animé)
  const LoadingIndicator = () => isLoading ? (
    <span className="inline-flex items-center ml-2">
      <span className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-pulse"></span>
    </span>
  ) : null;

  // Supprimé le blocage - on utilise displaySubscription avec valeurs par défaut

  const handleDownloadReceipt = () => {
    if (receiptRef.current) {
      receiptRef.current.downloadPDF();
    }
  };

  return (
    <PageContainer>
      <div className="py-4 sm:py-8">
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
          {/* En-tête */}
          <div className="mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-gray-200">
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-2 flex items-center">
              Gestion des Abonnements
              <LoadingIndicator />
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Gérez votre plan d'abonnement et consultez votre historique de facturation
            </p>
          </div>

          {/* Informations du plan actuel */}
          <div className="mb-6 sm:mb-8">
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-primary" />
                Abonnement actuel
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Informations de l'utilisateur */}
                <div className="flex items-center space-x-3 sm:space-x-4">
                  {user?.picture ? (
                    <img src={user.picture} alt="Profil" className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-gray-200" />
                  ) : (
                    <UserCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300" />
                  )}
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900">{user?.name || 'Utilisateur'}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">Administrateur du compte</p>
                  </div>
                </div>

                {/* Détails du plan */}
                <div className="text-center lg:text-left">
                  <div className="text-xs sm:text-sm text-gray-500 mb-1">Plan actuel</div>
                  <div className="text-lg sm:text-xl font-bold text-primary mb-2">{displaySubscription.currentPlan}</div>
                  
                  <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                    {modernSubscription && (
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                        modernSubscription.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : modernSubscription.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {modernSubscription.status === 'active' ? 'Actif' : 
                         modernSubscription.status === 'pending' ? 'En attente' : 'Inactif'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Informations simplifiées */}
                <div className="text-center">
                  <div className="text-xs sm:text-sm text-gray-500 mb-1">Tokens inclus</div>
                  <div className="text-xl sm:text-2xl font-bold text-primary mb-2">
                    {((subscription?.tokenBalance || 0) / 1000000).toFixed(1)}M
                  </div>
                  <div className="text-xs text-gray-600">
                    Inclus dans votre plan mensuel
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation par onglets */}
          <Tabs defaultValue="plan" onValueChange={(value) => setTab(value as any)}>
            <TabsList className="mb-6 sm:mb-8 w-full sm:w-auto">
              <TabsTrigger value="plan" className="flex items-center text-sm">
                <CreditCard className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Plans & Changements</span>
                <span className="sm:hidden">Plans</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center text-sm">
                <History className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Paiements</span>
                <span className="sm:hidden">Paiements</span>
                {pendingTransactions.length > 0 && (
                  <span className="ml-1.5 min-w-[18px] h-[18px] bg-yellow-500 text-white text-xs rounded-full flex items-center justify-center px-1">
                    {pendingTransactions.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Contenu des onglets */}
            <TabsContent value="plan">
              {tab === 'confirm' ? (
                <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 text-center">
                  <div className="flex items-center justify-center gap-2 text-xl sm:text-2xl text-green-600 font-bold mb-4">
                    <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8" />
                    Paiement réussi !
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                    Votre paiement a été traité avec succès. Votre abonnement sera mis à jour sous peu.
                  </p>
                  <button 
                    className="bg-primary text-white px-4 sm:px-6 py-2 rounded-md hover:bg-primary-dark transition-colors text-sm sm:text-base" 
                    onClick={() => {
                      setTab('plan');
                      setSelectedPlan(null);
                    }}
                  >
                    Retour aux plans
                  </button>
                </div>
              ) : (
                <PlanSelector 
                  plans={allPricingPlans} 
                  currentPlan={displaySubscription.currentPlan} 
                  onSelect={(plan) => { 
                    setSelectedPlan(plan);
                    // Ouvrir directement le modal de paiement
                    if (plan && typeof plan === 'object' && plan.price !== '0') {
                      setShowPaymentModal(true);
                    } else if (plan && typeof plan === 'object' && plan.price === '0') {
                      // Plan gratuit - activation directe
                      handleFreePlanActivation(plan);
                    }
                  }} 
                />
              )}
            </TabsContent>

            <TabsContent value="history">
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Paiements</h3>
                  {pendingTransactions.length > 0 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <Clock className="w-3 h-3 mr-1" />
                      {pendingTransactions.length} en attente
                    </span>
                  )}
                </div>
                
                {(pendingTransactions.length > 0 || displaySubscription.paymentHistory.length > 0) ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs sm:text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Méthode</th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                          <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Transactions en attente (en premier) */}
                        {pendingTransactions.map((tx) => (
                          <tr key={tx.transactionId} className="bg-yellow-50/50 hover:bg-yellow-50">
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-gray-900">
                              {new Date(tx.createdAt).toLocaleDateString('fr-FR', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-medium text-gray-900">
                              {tx.amount} {tx.currency}
                            </td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-gray-600 hidden sm:table-cell">
                              <span className="inline-flex items-center gap-1">
                                <Smartphone className="w-3.5 h-3.5 text-gray-500" />
                                {tx.provider}
                              </span>
                            </td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-gray-600">{tx.planName}</td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                              {tx.status === 'pending' && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  <Clock className="w-3 h-3 mr-1 animate-pulse" />
                                  En attente
                                </span>
                              )}
                              {tx.status === 'checking' && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                  Vérification...
                                </span>
                              )}
                              {tx.status === 'completed' && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Payé
                                </span>
                              )}
                              {tx.status === 'failed' && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Échoué
                                </span>
                              )}
                              {tx.status === 'expired' && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Expiré
                                </span>
                              )}
                            </td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                              {(tx.status === 'pending' || tx.status === 'checking') ? (
                                <div className="flex items-center gap-2">
                                  <button 
                                    className="inline-flex items-center text-primary hover:text-primary-dark font-medium text-xs sm:text-sm disabled:opacity-50"
                                    onClick={() => checkTransactionStatus(tx.transactionId)}
                                    disabled={tx.status === 'checking' || pendingLoading}
                                  >
                                    <RefreshCw className={`w-3 h-3 mr-1 ${tx.status === 'checking' ? 'animate-spin' : ''}`} />
                                    <span className="hidden sm:inline">Vérifier</span>
                                  </button>
                                  <button 
                                    className="text-gray-500 hover:text-primary p-1 rounded hover:bg-gray-100 transition-colors"
                                    onClick={() => setShowInstructions(tx)}
                                    title="Voir instructions USSD"
                                  >
                                    <Info className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <button 
                                  className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors"
                                  onClick={() => removeTransaction(tx.transactionId)}
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        
                        {/* Historique des paiements complétés */}
                        {displaySubscription.paymentHistory.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50">
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-gray-900">{payment.date}</td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap font-medium text-gray-900">${payment.amount}</td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-gray-600 hidden sm:table-cell">{payment.method}</td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-gray-600">{payment.plan}</td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                                payment.status === 'Payé' ? 'bg-green-100 text-green-800' :
                                payment.status === 'En attente' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {payment.status === 'Payé' && <CheckCircle className="w-3 h-3 mr-1" />}
                                {payment.status === 'En attente' && <Clock className="w-3 h-3 mr-1" />}
                                {payment.status === 'Échoué' && <XCircle className="w-3 h-3 mr-1" />}
                                {payment.status}
                              </span>
                            </td>
                            <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                              {payment.status === 'Payé' && (
                                <button 
                                  className="text-primary hover:text-primary-dark font-medium text-xs sm:text-sm"
                                  onClick={() => setShowReceipt(payment)}
                                >
                                  <span className="hidden sm:inline">Voir le reçu</span>
                                  <span className="sm:hidden">Reçu</span>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Aucun paiement effectué pour le moment</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Modal de reçu */}
        {showReceipt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowReceipt(null)}>
            <div className="bg-white rounded-xl max-w-4xl w-full relative m-4 flex flex-col" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
                <h3 className="font-semibold text-lg">Reçu de paiement</h3>
                <div className="flex items-center gap-2">
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 font-semibold transition-colors text-sm"
                    onClick={handleDownloadReceipt}
                  >
                    <Download size={14}/>
                    Télécharger PDF
                  </button>
                  <button className="text-gray-400 hover:text-primary" onClick={() => setShowReceipt(null)}>✕</button>
                </div>
              </div>
              <div className="p-2 sm:p-4 overflow-y-auto">
                <Receipt ref={receiptRef} payment={showReceipt} />
              </div>
            </div>
          </div>
        )}

        {/* Modal de paiement */}
        <PaymentModal
          plan={selectedPlan as PricingPlan}
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPlan(null);
            setFormError('');
            setPromoCode('');
            setPromoDiscount(0);
          }}
          onSubmit={handlePaymentSubmit}
          paymentType={paymentType}
          setPaymentType={setPaymentType}
          paymentInfo={paymentInfo}
          setPaymentInfo={setPaymentInfo}
          error={formError}
          billingPeriod={billingPeriod}
          setBillingPeriod={setBillingPeriod}
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          promoDiscount={promoDiscount}
          onValidatePromo={validatePromoCode}
          onSetPromoDiscount={setPromoDiscount}
        />

        {/* Modal d'instructions USSD */}
        {showInstructions && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowInstructions(null)}>
            <div className="bg-white rounded-xl max-w-md w-full relative m-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-primary" />
                  Instructions de paiement
                </h3>
                <button className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100" onClick={() => setShowInstructions(null)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                {/* Info transaction */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-yellow-800 font-medium mb-2">
                    <Clock className="w-4 h-4" />
                    Transaction en attente
                  </div>
                  <div className="text-sm text-yellow-700">
                    <p><strong>Plan :</strong> {showInstructions.planName}</p>
                    <p><strong>Montant :</strong> {showInstructions.amount} {showInstructions.currency}</p>
                    <p><strong>Opérateur :</strong> {showInstructions.provider}</p>
                    <p><strong>Téléphone :</strong> {showInstructions.phoneNumber}</p>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Comment confirmer le paiement</h4>
                  <p className="text-sm text-blue-700 whitespace-pre-wrap">{showInstructions.instructions}</p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button 
                    className="flex-1 bg-primary text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
                    onClick={() => {
                      checkTransactionStatus(showInstructions.transactionId);
                      setShowInstructions(null);
                    }}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Vérifier le statut
                  </button>
                  <button 
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    onClick={() => setShowInstructions(null)}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
